import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Snowflake, ShoppingCart, Check, AlertCircle, Loader2, CreditCard, Lock,
  ExternalLink, Wallet, Plus, Star, Ticket,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CATEGORY_TREE, PROFESSIONS, SECONDARY_PROFESSIONS } from '../data/shopProducts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

const WOW_ICON = (slug) =>
  `https://wow.zamimg.com/images/wow/icons/large/${slug}.jpg`;

// Servicios que se pueden canjear con un bono (VOUCHER_PRODUCTS en shop_router.py).
const VOUCHER_NAMES = {
  pase_nivel_80:      'Subida a 80',
  maestria_profesion: 'Maestría en 2 profesiones',
  paquete_vuelo:      'Vuelo Ártico',
  cambio_faccion:     'Cambio de Facción',
  caja_fuerte_mercenario: 'Alijo del Mercenario',
};
const voucherSummary = (vouchers) =>
  Object.entries(vouchers ?? {})
    .filter(([, n]) => n > 0)
    .map(([id, n]) => `${n} × ${VOUCHER_NAMES[id] ?? id}`)
    .join(' · ');

const QUALITY_KEYS = {
  legendary: { border: 'rgba(255,140,0,0.70)', glow: 'rgba(255,120,0,0.22)', color: '#ff8c00', badgeBg: 'rgba(90,48,0,0.90)', key: 'legendary' },
  epic:      { border: 'rgba(168,85,247,0.60)', glow: 'rgba(168,85,247,0.20)', color: '#a855f7', badgeBg: 'rgba(58,26,106,0.90)', key: 'epic' },
  rare:      { border: 'rgba(59,130,246,0.55)', glow: 'rgba(59,130,246,0.15)', color: '#3b82f6', badgeBg: 'rgba(26,58,122,0.90)', key: 'rare' },
  uncommon:  { border: 'rgba(34,197,94,0.55)',  glow: 'rgba(34,197,94,0.12)',  color: '#22c55e', badgeBg: 'rgba(10,48,16,0.90)', key: 'uncommon' },
};

// ─── Tooltip-style product card (estilo WoW) ───────────────────────────────
function ProductCard({ product, onBuy, isLoggedIn, vouchersLeft = 0 }) {
  const q = QUALITY_KEYS[product.quality] ?? QUALITY_KEYS.rare;
  const [imgErr, setImgErr] = useState(false);
  const [hover, setHover]   = useState(false);
  const { t } = useTranslation();

  const isLegendary = product.quality === 'legendary';
  const bindText = product.action_type === 'send_items' ? 'Entrega por correo'
    : product.action_type === 'voucher' ? 'Bonos para tu cuenta' : 'Aplicado al personaje';

  return (
    <article
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        borderRadius: 8,
        padding: 14,
        background: 'linear-gradient(180deg, rgba(2,6,14,0.96), rgba(0,4,10,0.98))',
        border: `1px solid ${q.border}`,
        boxShadow: `0 0 ${isLegendary ? 24 : 18}px ${q.glow}, inset 0 0 0 1px ${q.glow}`,
        display: 'grid',
        gridTemplateColumns: '64px 1fr',
        columnGap: 14,
        transition: 'transform 0.2s ease, border-color 0.2s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        cursor: 'pointer',
      }}
      onClick={() => onBuy(product)}
    >
      {!imgErr ? (
        <img
          src={WOW_ICON(product.icon)}
          alt={product.name}
          onError={() => setImgErr(true)}
          style={{
            width: 64, height: 64,
            borderRadius: 8,
            border: `2px solid ${q.color}`,
            boxShadow: `0 0 ${isLegendary ? 16 : 12}px ${q.glow}`,
            objectFit: 'cover',
          }}
        />
      ) : (
        <div style={{
          width: 64, height: 64, borderRadius: 8,
          border: `2px solid ${q.color}`,
          background: '#0a0e1a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 12px ${q.glow}`,
        }}>
          <ShoppingCart size={22} style={{ color: q.color }} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{
          fontSize: 13.5, fontWeight: 700,
          color: q.color,
          lineHeight: 1.2,
          letterSpacing: '0.01em',
          wordWrap: 'break-word',
          paddingRight: product.badge ? 80 : 0,
        }}>
          {product.name}
        </div>
        {product.subtitle && (
          <div style={{
            fontSize: 10.5, fontStyle: 'italic',
            color: 'rgba(255,255,255,0.45)',
            marginTop: 2, lineHeight: 1.4,
          }}>
            «{product.subtitle}»
          </div>
        )}
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.78)',
          marginTop: 6, lineHeight: 1.4,
        }}>
          {bindText}
        </div>
        <ul style={{
          listStyle: 'none', margin: '6px 0 0', padding: 0,
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {product.features.map((f) => (
            <li key={f} style={{ fontSize: 11.5, color: '#6ee7ff', lineHeight: 1.4 }}>
              <span style={{ opacity: 0.5 }}>+ </span>{f}
            </li>
          ))}
        </ul>
      </div>

      {vouchersLeft > 0 && (
        <div style={{
          gridColumn: '1 / -1',
          marginTop: 10, padding: '6px 10px', borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.35)',
          color: '#fcd34d', fontSize: 11.5, fontWeight: 600,
        }}>
          <Ticket size={13} />
          {vouchersLeft === 1 ? 'Tienes 1 bono para canjear aquí' : `Tienes ${vouchersLeft} bonos para canjear aquí`}
        </div>
      )}

      <div style={{
        gridColumn: '1 / -1',
        marginTop: 12, paddingTop: 12,
        borderTop: '1px dashed rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'Cinzel, serif',
          fontSize: 18, color: '#fff', fontWeight: 700,
        }}>
          <Snowflake size={16} style={{ color: '#00e5ff', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.5))' }} />
          {product.price_crystals.toLocaleString()}
          <small style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500, fontSize: 10,
            color: 'rgba(255,255,255,0.55)',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            marginLeft: 4,
          }}>
            cristales
          </small>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onBuy(product); }}
          style={{
            padding: '7px 14px',
            borderRadius: 4,
            background: hover ? '#00e5ff' : 'rgba(0,229,255,0.1)',
            border: '1px solid rgba(0,229,255,0.4)',
            color: hover ? '#021019' : '#00e5ff',
            fontSize: 11, fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'background 0.2s ease, color 0.2s ease',
          }}
        >
          {vouchersLeft > 0 ? 'Canjear bono' : isLegendary ? 'Reclamar' : (isLoggedIn ? t('shop.buy') : t('shop.enter'))}
        </button>
      </div>

      {product.badge && (
        <span style={{
          position: 'absolute', top: 10, right: 12,
          fontFamily: 'Inter, monospace',
          fontSize: 9.5, fontWeight: 700,
          letterSpacing: '0.16em',
          color: q.color, opacity: 0.9,
          textTransform: 'uppercase',
        }}>
          {product.badge}
        </span>
      )}
    </article>
  );
}

// ─── Buy modal (intacto del original) ──────────────────────────────────────
function BuyModal({ product, user, crystals, onClose, onSuccess }) {
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState('');
  const [prof1, setProf1] = useState('');
  const [prof2, setProf2] = useState('');
  const [secProf, setSecProf] = useState('');
  const [itemOption, setItemOption] = useState('');
  const [useVoucher, setUseVoucher] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingChars, setFetchingChars] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const isDualProf = product?.action_type === 'dual_profession';
  const isSecProf  = product?.action_type === 'secondary_profession';
  // Productos con variantes (item_options): el jugador elige un objeto (p. ej. la raza de la camisa racial)
  const itemOptions = product?.item_options ?? [];
  const hasOptions  = itemOptions.length > 0;
  const isVoucherProduct = product?.action_type === 'voucher';
  const vouchersLeft = product?.voucher_redeemable ? (user?.vouchers?.[product.id] ?? 0) : 0;
  const payWithVoucher = useVoucher && vouchersLeft > 0;
  const canAfford = payWithVoucher || crystals >= (product?.price_crystals ?? 0);
  const q = product ? (QUALITY_KEYS[product.quality] ?? QUALITY_KEYS.rare) : QUALITY_KEYS.rare;

  const loadCharacters = (keepSelection) => {
    setFetchingChars(true);
    axios
      .get(`${BACKEND_URL}/api/account/characters`, { withCredentials: true })
      .then((res) => {
        const chars = res.data?.characters ?? res.data ?? [];
        setCharacters(chars);
        if (!keepSelection && chars.length > 0) setSelectedChar(chars[0].name);
      })
      .catch(() => setError(t('shop.char_load_error')))
      .finally(() => setFetchingChars(false));
  };

  useEffect(() => {
    if (!product) return;
    setError(''); setSuccess(false);
    setProf1(''); setProf2(''); setSecProf(''); setItemOption('');
    setUseVoucher((user?.vouchers?.[product.id] ?? 0) > 0);
    if (product.action_type === 'voucher') { setFetchingChars(false); return; }
    loadCharacters(false);
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  // Estado de profesiones del personaje seleccionado
  const selCharObj  = characters.find((c) => c.name === selectedChar);
  const primaries   = selCharObj?.professions ?? [];
  const secondaries = selCharObj?.secondary_professions ?? [];
  const MAX_SKILL   = 450;
  // Maestría dual: 0 = elegir 2, 2 = subir las actuales, 1 = bloqueado
  const dualNeedsPick = isDualProf && primaries.length === 0;
  const dualUpgrade   = isDualProf && primaries.length === 2;
  const dualBlocked   = isDualProf && primaries.length === 1;
  const dualMaxed     = dualUpgrade && primaries.every((p) => p.value >= MAX_SKILL);
  // Secundaria elegida en el desplegable
  const secCurrent = isSecProf && secProf
    ? secondaries.find((s) => s.id === parseInt(secProf))
    : null;
  const secMaxed = !!secCurrent && secCurrent.value >= MAX_SKILL;

  const isReady = () => {
    if (isVoucherProduct) return canAfford;
    if (!selectedChar) return false;
    if (dualBlocked || dualMaxed) return false;
    if (dualNeedsPick && (!prof1 || !prof2 || prof1 === prof2)) return false;
    if (isSecProf && (!secProf || secMaxed)) return false;
    if (hasOptions && !itemOption) return false;
    return canAfford;
  };

  const handleConfirm = async () => {
    if (!isReady()) return;
    setLoading(true); setError('');
    try {
      const body = isVoucherProduct
        ? { product_id: product.id }
        : { product_id: product.id, character_name: selectedChar, use_voucher: payWithVoucher };
      if (dualNeedsPick) { body.profession1 = parseInt(prof1); body.profession2 = parseInt(prof2); }
      if (isSecProf) { body.profession1 = parseInt(secProf); }
      if (hasOptions) { body.item_id = parseInt(itemOption); }
      const { data: res } = await axios.post(`${BACKEND_URL}/api/shop/buy`, body, { withCredentials: true });
      onSuccess(payWithVoucher ? 0 : product.price_crystals, res.frost_crystals, res.vouchers);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail ?? t('shop.buy_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  const prof2Options = PROFESSIONS.filter((p) => String(p.id) !== prof1);

  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md"
        style={{ background: 'rgba(8,12,28,0.97)', border: `1px solid ${q.border}`, boxShadow: `0 0 40px ${q.glow}` }}
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-3">
            <img src={WOW_ICON(product.icon)} alt={product.name}
              className="w-10 h-10 rounded-lg"
              style={{ border: `2px solid ${q.border}` }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div className="text-base">{product.name}</div>
              <div className="text-xs font-normal mt-0.5" style={{ color: q.color }}>{t(`shop.${q.key}`)}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-full"
              style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.45)' }}>
              <Check size={30} className="text-green-400" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">{t('shop.success_title')}</p>
              <p className="text-slate-300 text-sm mt-1.5 leading-relaxed">
                {isVoucherProduct
                  ? 'Los bonos ya están en tu cuenta. Para usarlos, abre el servicio (Subida a 80, Maestría, Alijo del Mercenario o Cambio de Facción) y elige «Usar un bono».'
                  : product.applies === 'relog' ? t('shop.success_relog_body')
                  : (product.applies === 'login' || isDualProf || isSecProf) ? t('shop.success_prof_body') : t('shop.success_body')}
              </p>
            </div>
            <Button onClick={onClose} className="mt-1 w-full"
              style={{ background: `linear-gradient(135deg, ${q.border}, rgba(0,0,0,0.4))`, border: `1px solid ${q.border}`, color: '#fff' }}>
              {t('shop.success_close')}
            </Button>
          </div>
        ) : (
        <>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex items-center justify-between rounded-lg px-4 py-3"
            style={{ background: 'rgba(0,180,255,0.06)', border: '1px solid rgba(0,180,255,0.15)' }}>
            <span className="text-slate-300 text-sm">{t('shop.cost')}</span>
            {payWithVoucher ? (
              <div className="flex items-center gap-1.5">
                <Ticket size={14} className="text-amber-300" />
                <span className="text-white font-bold">1 bono</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Snowflake size={14} className="text-cyan-400" />
                <span className="text-white font-bold">{product.price_crystals.toLocaleString()}</span>
                <span className="text-slate-400 text-xs">{t('shop.crystals')}</span>
              </div>
            )}
          </div>

          {vouchersLeft > 0 && (
            <label className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-amber-200 cursor-pointer"
              style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.3)' }}>
              <input type="checkbox" checked={useVoucher} onChange={(e) => setUseVoucher(e.target.checked)} />
              <span>Usar un bono (te {vouchersLeft === 1 ? 'queda 1' : `quedan ${vouchersLeft}`})</span>
            </label>
          )}

          {isVoucherProduct && (
            <div className="flex flex-col gap-1 rounded-lg p-3 text-xs text-slate-300"
              style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <span className="text-amber-200 font-semibold">Recibes en tu cuenta:</span>
              <span>{voucherSummary(product.grants)}</span>
              <span className="text-slate-400">Los canjeas cuando quieras, en el personaje que elijas, desde cada servicio. No caducan.</span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm px-1">
            <span className="text-slate-400">{t('shop.balance')}</span>
            <span className={`font-semibold ${canAfford ? 'text-cyan-300' : 'text-red-400'}`}>
              {crystals.toLocaleString()} {t('shop.crystals')}
            </span>
          </div>

          {!canAfford && (
            <div className="flex items-center gap-2 text-xs text-red-400 px-1">
              <AlertCircle size={13} /> {t('shop.insufficient')}
            </div>
          )}

          {!isVoucherProduct && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-slate-300">{t('shop.char_label')}</label>
            {fetchingChars ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 size={14} className="animate-spin" /> {t('shop.loading_chars')}
              </div>
            ) : characters.length === 0 ? (
              <p className="text-xs text-yellow-400">{t('shop.no_chars')}</p>
            ) : (
              <Select value={selectedChar} onValueChange={setSelectedChar}>
                <SelectTrigger className="bg-black/30 text-white" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <SelectValue placeholder={t('shop.select_char')} />
                </SelectTrigger>
                <SelectContent style={{ background: 'rgba(10,14,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {characters.map((c) => (
                    <SelectItem key={c.name} value={c.name} className="text-white">
                      {c.name}{c.level && <span className="text-slate-400 ml-2 text-xs">Nv. {c.level}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          )}

          {dualNeedsPick && (
            <div className="flex flex-col gap-3 rounded-lg p-3"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs text-slate-400">{t('shop.dual_hint')}</p>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-slate-300">{t('shop.prof1')}</label>
                <Select value={prof1} onValueChange={setProf1}>
                  <SelectTrigger className="bg-black/30 text-white" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                    <SelectValue placeholder={t('shop.select_prof')} />
                  </SelectTrigger>
                  <SelectContent style={{ background: 'rgba(10,14,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {PROFESSIONS.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)} className="text-white">
                        <div className="flex items-center gap-2">
                          <img src={WOW_ICON(p.icon)} alt={p.name} className="w-4 h-4 rounded"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-slate-300">{t('shop.prof2')}</label>
                <Select value={prof2} onValueChange={setProf2} disabled={!prof1}>
                  <SelectTrigger className="bg-black/30 text-white" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                    <SelectValue placeholder={prof1 ? t('shop.select_prof') : t('shop.select_prof1_first')} />
                  </SelectTrigger>
                  <SelectContent style={{ background: 'rgba(10,14,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {prof2Options.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)} className="text-white">
                        <div className="flex items-center gap-2">
                          <img src={WOW_ICON(p.icon)} alt={p.name} className="w-4 h-4 rounded"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {prof1 && prof2 && prof1 === prof2 && (
                <p className="text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle size={12} /> {t('shop.prof_diff')}
                </p>
              )}
            </div>
          )}

          {dualUpgrade && (
            <div className="flex flex-col gap-2 rounded-lg p-3"
              style={{ background: dualMaxed ? 'rgba(234,179,8,0.06)' : 'rgba(34,197,94,0.06)',
                       border: `1px solid ${dualMaxed ? 'rgba(234,179,8,0.25)' : 'rgba(34,197,94,0.25)'}` }}>
              <p className="text-xs text-slate-300">
                {dualMaxed ? t('shop.already_maxed') : t('shop.dual_upgrade_hint')}
              </p>
              {primaries.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm text-white">
                  <span>{p.name}</span>
                  <span className="text-slate-400 text-xs">
                    {p.value >= MAX_SKILL ? 'Nv. 450 (máx)' : `Nv. ${p.value} → 450`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {dualBlocked && (
            <div className="flex items-start gap-2 rounded-lg p-3 text-xs text-yellow-300"
              style={{ background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.25)' }}>
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{t('shop.dual_blocked', { prof: primaries[0]?.name })}</span>
            </div>
          )}

          {isSecProf && (
            <div className="flex flex-col gap-2 rounded-lg p-3"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <label className="text-xs text-slate-300">{t('shop.secondary_label')}</label>
              <Select value={secProf} onValueChange={setSecProf}>
                <SelectTrigger className="bg-black/30 text-white" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <SelectValue placeholder={t('shop.select_prof')} />
                </SelectTrigger>
                <SelectContent style={{ background: 'rgba(10,14,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {SECONDARY_PROFESSIONS.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)} className="text-white">
                      <div className="flex items-center gap-2">
                        <img src={WOW_ICON(p.icon)} alt={p.name} className="w-4 h-4 rounded"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {secProf && (
                secMaxed ? (
                  <p className="text-xs text-yellow-300 flex items-center gap-1">
                    <AlertCircle size={12} /> {t('shop.already_maxed')}
                  </p>
                ) : (
                  <p className="text-xs text-slate-300">
                    {secCurrent
                      ? t('shop.single_upgrade', { level: secCurrent.value })
                      : t('shop.single_learn')}
                  </p>
                )
              )}
            </div>
          )}

          {hasOptions && (
            <div className="flex flex-col gap-2 rounded-lg p-3"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <label className="text-xs text-slate-300">{product.option_label || t('shop.option_label')}</label>
              <Select value={itemOption} onValueChange={setItemOption}>
                <SelectTrigger className="bg-black/30 text-white" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <SelectValue placeholder={t('shop.select_option')} />
                </SelectTrigger>
                <SelectContent style={{ background: 'rgba(10,14,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {itemOptions.map((o) => (
                    <SelectItem key={o.item_id} value={String(o.item_id)} className="text-white">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {product.applies && (
            <div className="flex items-start gap-2 rounded-lg p-3 text-xs text-slate-200"
              style={{ background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.3)' }}>
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                {product.applies === 'relog'
                  ? <>Se aplica en la pantalla de personajes: si estás <strong>conectado</strong> con él, cierra sesión y vuelve a entrar.</>
                  : <>Si el personaje está <strong>conectado</strong>, se aplica al momento; si no, en cuanto entres al juego con él.</>}
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-400 px-1">
              <AlertCircle size={13} /> {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-slate-300 hover:bg-white/10">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !isReady() || fetchingChars}
            style={{
              background: isReady() ? `linear-gradient(135deg, ${q.border}, rgba(0,0,0,0.4))` : 'rgba(60,60,80,0.5)',
              border: `1px solid ${q.border}`,
              color: '#fff',
              opacity: loading || !isReady() || fetchingChars ? 0.6 : 1,
            }}
          >
            {loading
              ? <><Loader2 size={14} className="animate-spin" /> {t('shop.processing')}</>
              : <><Check size={14} /> {t('shop.confirm')}</>
            }
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Crystal pack modal (intacto) ──────────────────────────────────────────
const CRYSTAL_PACK_DISPLAY = [
  { id: 'pack_500',  crystals: 500,  price_eur: 5,  label: 'Iniciado' },
  { id: 'pack_1200', crystals: 1200, price_eur: 10, label: 'Aventurero' },
  { id: 'pack_2500', crystals: 2500, price_eur: 20, label: 'Heroe' },
  { id: 'pack_5000', crystals: 5500, price_eur: 40, label: 'Campeon' },
];

function BuyCrystalsModal({ open, onClose, user, crystals }) {
  const [loading, setLoading]     = useState(null);
  const [error, setError]         = useState('');
  const [custom, setCustom]       = useState('');
  const [tab, setTab]             = useState('packs');

  if (!open) return null;

  const handleBuyPack = async (packId) => {
    if (!user) { window.location.href = '/login'; return; }
    setLoading(packId); setError('');
    const pack = CRYSTAL_PACK_DISPLAY.find(p => p.id === packId);
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'EUR',
        value: pack?.price_eur,
        items: [{ item_id: packId, item_name: pack?.label }],
      });
    }
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payment/checkout`,
        { pack_id: packId },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail ?? 'Error al iniciar el pago. Inténtalo de nuevo.');
      setLoading(null);
    }
  };

  const handleBuyCustom = async () => {
    const amount = parseInt(custom, 10);
    if (!amount || amount < 100) { setError('El mínimo son 100 crystals.'); return; }
    if (amount % 100 !== 0) { setError('La cantidad debe ser múltiplo de 100.'); return; }
    if (!user) { window.location.href = '/login'; return; }
    setLoading('custom'); setError('');
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'EUR',
        value: amount / 100,
        items: [{ item_id: 'custom', item_name: `${amount} cristales personalizados` }],
      });
    }
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payment/checkout`,
        { crystals: amount },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail ?? 'Error al iniciar el pago. Inténtalo de nuevo.');
      setLoading(null);
    }
  };

  const customAmount  = parseInt(custom, 10) || 0;
  const customEur     = customAmount >= 100 ? (customAmount / 100).toFixed(2) : '—';

  return (
    <div
      role="presentation"
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)', zIndex: 10000 }}
      onClick={onClose}
      onKeyDown={() => {}}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg mx-4 rounded-2xl p-6"
        style={{ background: 'rgba(8,12,28,0.97)', border: '1px solid rgba(0,180,255,0.3)', boxShadow: '0 0 40px rgba(0,120,200,0.2)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
        >
          ✕
        </button>

        <div className="flex items-center gap-3 mb-1">
          <Snowflake size={24} className="text-cyan-400" />
          <h2 className="text-white font-bold text-lg">Comprar Cristales de Escarcha</h2>
        </div>
        <p className="text-slate-400 text-sm mb-1">
          Los cristales son permanentes y se usan en la tienda.
        </p>
        <div className="flex items-center gap-2 mb-4 text-xs px-3 py-1.5 rounded-lg w-fit"
          style={{ background: 'rgba(0,180,255,0.07)', border: '1px solid rgba(0,180,255,0.2)', color: 'rgba(0,229,255,0.8)' }}>
          <Snowflake size={11} />
          <span>1 € = 100 Cristales de Escarcha</span>
        </div>

        {user && (
          <div className="flex items-center gap-2 mb-5">
            <Wallet size={16} className="text-cyan-400" />
            <span className="text-slate-300 text-sm">Tus cristales actuales: <strong className="text-cyan-300">{crystals?.toLocaleString() || 0}</strong></span>
          </div>
        )}

        <div className="flex gap-2 mb-4">
          {['packs', 'custom'].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(''); }}
              className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={{
                background: tab === t ? 'rgba(0,150,220,0.25)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${tab === t ? 'rgba(0,229,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: tab === t ? 'rgba(0,229,255,0.95)' : 'rgba(255,255,255,0.4)',
              }}
            >
              {t === 'packs' ? 'Packs' : 'Cantidad personalizada'}
            </button>
          ))}
        </div>

        {tab === 'packs' ? (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {CRYSTAL_PACK_DISPLAY.map(pack => (
              <button
                key={pack.id}
                onClick={() => handleBuyPack(pack.id)}
                disabled={!!loading}
                className="flex flex-col items-center gap-1.5 p-4 rounded-xl transition-all hover:brightness-125 active:scale-95 disabled:opacity-60"
                style={{ background: 'rgba(0,150,220,0.1)', border: '1px solid rgba(0,180,255,0.25)' }}
              >
                <Snowflake size={20} className="text-cyan-400" />
                <span className="text-white font-bold text-lg">{pack.crystals.toLocaleString()}</span>
                <span className="text-cyan-300 text-xs font-semibold">{pack.label}</span>
                <span className="text-slate-400 text-xs">{pack.price_eur} €</span>
                {loading === pack.id && <Loader2 size={14} className="animate-spin text-cyan-400 mt-1" />}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 input-wrap" style={{ border: '1px solid rgba(0,180,255,0.3)', borderRadius: 8 }}>
                <Snowflake size={15} className="input-icon text-cyan-400" />
                <input
                  type="number"
                  min={100}
                  step={100}
                  value={custom}
                  onChange={e => { setCustom(e.target.value); setError(''); }}
                  placeholder="Cantidad de crystals (min. 100)"
                  className="form-input"
                  style={{ background: 'transparent' }}
                />
              </div>
            </div>
            {customAmount >= 100 && (
              <p className="text-sm text-center" style={{ color: 'rgba(0,229,255,0.8)' }}>
                {customAmount.toLocaleString()} crystals = <strong>{customEur} €</strong>
              </p>
            )}
            <button
              onClick={handleBuyCustom}
              disabled={!!loading || customAmount < 100}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all hover:brightness-125 active:scale-95 disabled:opacity-50"
              style={{ background: 'rgba(0,150,220,0.2)', border: '1px solid rgba(0,229,255,0.4)', color: '#fff' }}
            >
              {loading === 'custom' ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              {loading === 'custom' ? 'Procesando...' : 'Comprar'}
            </button>
            <p className="text-xs text-center text-slate-500">Múltiplos de 100. Mínimo 100 crystals (1 €).</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400 mb-3 px-1">
            <AlertCircle size={13} /> {error}
          </div>
        )}

        <p className="text-center text-xs text-slate-500">
          Pago seguro via Stripe. No reembolsable. Al comprar aceptas los{' '}
          <a href="/terminos" className="text-cyan-400 hover:text-cyan-300">Terminos de Servicio</a>.
        </p>
      </div>
    </div>
  );
}

// ─── Layout config ─────────────────────────────────────────────────────────
const CHAPTERS = [
  { key: 'comandante',   num: '01', title: ['El', 'Comandante'], blurb: 'Servicios, profesiones y movilidad para empezar fuerte sin perder el tiempo.', locked: false },
  { key: 'trono_helado', num: '02', title: ['Trono', 'Helado'],  blurb: 'Monturas, mascotas, consumibles y apariencias — lo cosmético que cuenta tu historia en Azeroth.', locked: false },
];

const SUBCAT_SUBLABEL = {
  warband:     'bonos para tus alters',
  servicios:   'atajos al endgame',
  profesiones: 'elige tus 2 especialidades',
  movilidad:   'vuelo y monturas iniciales',
  monturas:    'vuelo & terrestres exclusivas',
  mascotas:    'compañeros de viaje',
  consumibles: 'packs de 20 unidades',
  apariencias: 'transmog & tabardos',
};

// ─── Sub-blocks ────────────────────────────────────────────────────────────
function SectionHead({ title, sublabel, count }) {
  return (
    <div className="shop-section-head" style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      marginBottom: 18, paddingBottom: 10,
      borderBottom: '1px solid rgba(0,229,255,0.14)',
    }}>
      <h3 style={{
        fontFamily: 'Cinzel, serif',
        fontSize: 22, fontWeight: 700, color: '#fff',
        letterSpacing: '0.04em', textTransform: 'capitalize',
      }}>
        {title}
        {sublabel && (
          <small style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 500,
            fontSize: 12, color: 'rgba(255,255,255,0.55)',
            marginLeft: 10, letterSpacing: '0.06em',
          }}>
            {sublabel}
          </small>
        )}
      </h3>
      {count != null && (
        <span style={{
          fontFamily: 'Inter, monospace',
          fontSize: 11, color: 'rgba(255,255,255,0.55)',
          letterSpacing: '0.12em',
        }}>
          {count} {count === 1 ? 'ítem' : 'ítems'}
        </span>
      )}
    </div>
  );
}

function ChapterDivider({ num, title, blurb, isFirst }) {
  return (
    <div className="shop-chapter-divider" style={{
      display: 'flex', alignItems: 'center', gap: 14,
      margin: isFirst ? '0 0 16px' : '36px 0 16px',
      padding: '14px 0 0',
      borderTop: isFirst ? 'none' : '1px solid rgba(0,229,255,0.14)',
    }}>
      <h2 style={{
        fontFamily: 'Cinzel, serif',
        fontSize: 22, fontWeight: 700, color: '#fff',
        letterSpacing: '0.06em',
      }}>
        {title[0]}{' '}
        <span style={{ color: '#00e5ff', textShadow: '0 0 14px rgba(0,229,255,0.4)' }}>
          {title[1]}
        </span>
      </h2>
      <span style={{
        flex: 1, height: 1,
        background: 'linear-gradient(to right, rgba(0,229,255,0.35), transparent)',
      }} />
      <span className="shop-chapter-blurb" style={{
        color: 'rgba(255,255,255,0.55)', fontSize: 12,
        letterSpacing: '0.04em', maxWidth: 360, lineHeight: 1.45,
      }}>
        {blurb}
      </span>
    </div>
  );
}

// ─── Rail ──────────────────────────────────────────────────────────────────
function CatalogRail({ activeMain, activeSub, onSelectMain, onSelectSub, productCounts }) {
  return (
    <aside style={{
      background: 'rgba(0,8,22,0.65)',
      border: '1px solid rgba(0,229,255,0.14)',
      borderRadius: 12,
      padding: 18,
      height: 'fit-content',
      position: 'sticky', top: 16,
      alignSelf: 'start',
    }}>
      <h3 style={{
        fontFamily: 'Cinzel, serif',
        fontSize: 12, color: 'rgba(0,229,255,0.7)',
        letterSpacing: '0.18em', textTransform: 'uppercase',
        marginBottom: 14,
      }}>
        Catálogo
      </h3>

      {CHAPTERS.map((ch) => {
        const subs = CATEGORY_TREE[ch.key]?.subs ?? [];
        const isActive = activeMain === ch.key;
        const count = productCounts[ch.key] ?? 0;

        return (
          <div key={ch.key} style={{ marginBottom: 18 }}>
            <button
              onClick={() => !ch.locked && onSelectMain(ch.key)}
              disabled={ch.locked}
              style={{
                width: '100%',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '9px 12px', borderRadius: 8,
                fontSize: 11.5, fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: ch.locked ? 'default' : 'pointer',
                marginBottom: 3,
                background: isActive ? 'rgba(0,229,255,0.1)' : 'transparent',
                color: ch.locked ? 'rgba(255,210,120,0.65)' : (isActive ? '#00e5ff' : 'rgba(255,255,255,0.78)'),
                border: 'none',
                boxShadow: isActive ? 'inset 2px 0 0 #00e5ff' : 'none',
                transition: 'background 0.15s ease',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {ch.locked && <Lock size={12} />}
                {CATEGORY_TREE[ch.key]?.label ?? ch.key}
              </span>
              <span style={{
                fontFamily: 'Inter, monospace',
                fontSize: 10,
                color: ch.locked ? 'rgba(255,210,120,0.45)' : 'rgba(255,255,255,0.35)',
              }}>
                {ch.locked ? '🔒' : String(count).padStart(2, '0')}
              </span>
            </button>

            {!ch.locked && (
              <ul style={{
                listStyle: 'none', padding: '0 0 0 18px', margin: '4px 0 8px',
                borderLeft: '1px solid rgba(0,229,255,0.14)',
              }}>
                <li
                  onClick={() => { onSelectMain(ch.key); onSelectSub(null); }}
                  style={{
                    padding: '5px 8px', fontSize: 11, cursor: 'pointer',
                    color: isActive && !activeSub ? '#00e5ff' : 'rgba(255,255,255,0.55)',
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}
                >
                  Todos
                </li>
                {subs.map((s) => (
                  <li
                    key={s.key}
                    onClick={() => { onSelectMain(ch.key); onSelectSub(s.key); }}
                    style={{
                      padding: '5px 8px', fontSize: 11, cursor: 'pointer',
                      color: isActive && activeSub === s.key ? '#00e5ff' : 'rgba(255,255,255,0.55)',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}
                  >
                    {s.label}
                  </li>
                ))}
              </ul>
            )}

          </div>
        );
      })}
    </aside>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function ShopPage() {
  const [activeMain, setActiveMain]       = useState(null);
  const [activeSub, setActiveSub]         = useState(null);
  const [user, setUser]                   = useState(null);
  const [crystals, setCrystals]           = useState(0);
  const [loadingUser, setLoadingUser]     = useState(true);
  const [products, setProducts]           = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [successMsg, setSuccessMsg]       = useState('');
  const [showBuyCrystals, setShowBuyCrystals] = useState(false);
  const [paymentSuccess, setPaymentSuccess]   = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentSuccess(true);
      window.history.replaceState({}, '', '/tienda');
    }
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); setCrystals(r.data.frost_crystals ?? 0); })
      .catch(() => setUser(null))
      .finally(() => setLoadingUser(false));

    // Con sesión de admin el backend incluye los productos ocultos (hidden): se marcan para distinguirlos
    axios.get(`${BACKEND_URL}/api/shop/products`, { withCredentials: true })
      .then((r) => setProducts(r.data.map((p) => (p.hidden ? { ...p, badge: 'OCULTO' } : p))))
      .catch(() => setProducts([]));
  }, []);

  // Solo productos de categorías con capítulo en la tienda.
  const visibleProducts = products.filter((p) => CHAPTERS.some((ch) => ch.key === p.category));

  const productCounts = visibleProducts.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] ?? 0) + 1;
    return acc;
  }, {});

  const handleMainChange = (key) => {
    setActiveMain((prev) => (prev === key ? null : key));
    setActiveSub(null);
  };

  const handleBuy = (product) => {
    if (!user) { window.location.href = '/login'; return; }
    setSelectedProduct(product); setSuccessMsg('');
  };

  const handleSuccess = (cost, newBalance, vouchers) => {
    if (vouchers) setUser((u) => (u ? { ...u, vouchers } : u));
    // Solo actualiza el saldo; el modal muestra la confirmación in-situ y lo
    // cierra el usuario. El toast queda como refuerzo tras cerrar.
    setCrystals(newBalance !== undefined ? newBalance : (p) => p - cost);
    setSuccessMsg(t('shop.success'));
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  // Decide qué capítulos renderizar
  const chaptersToShow = CHAPTERS.filter((ch) => {
    if (ch.locked) return false;
    if (activeMain === null) return true;
    return activeMain === ch.key;
  });

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Tienda" noIndex />
      <Navbar />

      <main className="shop-pg-main" style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 24px 80px' }}>

        {/* ─── Header (eyebrow + título + sub + pills + wallet) ─── */}
        <header
          className="anim-fade-up shop-pg-header"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'end',
            gap: 28,
            marginBottom: 26,
            paddingBottom: 26,
            borderBottom: '1px solid rgba(0,229,255,0.14)',
          }}
        >
          <div>
            {/* Eyebrow row: on mobile shows wallet chip inline */}
            <div className="shop-pg-eyebrow-row">
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                fontSize: 11, fontWeight: 700,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: 'rgba(0,229,255,0.7)',
              }}>
                <span style={{ width: 24, height: 1, background: 'rgba(0,229,255,0.7)' }} />
                Tienda de Frostbound
              </span>
              {/* Mobile-only wallet chip */}
              <div className="shop-pg-wallet-mobile" style={{ display: 'none' }}>
                <div className="shop-wallet-chip">
                  <Snowflake size={14} style={{ color: '#00e5ff', filter: 'drop-shadow(0 0 5px rgba(0,229,255,0.6))' }} />
                  {loadingUser ? '—' : (user ? crystals.toLocaleString() : '—')}
                </div>
                <button
                  className="shop-wallet-plus"
                  onClick={() => user ? setShowBuyCrystals(true) : (window.location.href = '/login')}
                >
                  <Plus size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <h1 className="shop-h1" style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 34, fontWeight: 700,
              lineHeight: 1.08, letterSpacing: '0.02em',
              color: '#fff',
            }}>
              Apoya el servidor.<br />
              Cada aporte mantiene{' '}
              <span style={{ color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.4)' }}>
                Frostbound
              </span>
            </h1>
            <p className="shop-sub" style={{
              marginTop: 12, color: 'rgba(255,255,255,0.55)',
              fontSize: 14, lineHeight: 1.55, maxWidth: 560,
            }}>
              Servicios, cosméticos y calidad de vida: nada que dé poder en combate.
              El equipo se consigue jugando.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
              {[
                { label: 'sin pay-to-win', dot: true },
                { label: 'el equipo se gana jugando' },
                { label: 'cada aporte sostiene el servidor' },
              ].map((p) => (
                <span key={p.label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 11px', borderRadius: 9999,
                  border: '1px solid rgba(0,229,255,0.14)',
                  background: 'rgba(0,18,38,0.6)',
                  fontSize: 10.5, fontWeight: 600,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.78)',
                }}>
                  {p.dot && <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: '#00e5ff', boxShadow: '0 0 8px #00e5ff',
                  }} />}
                  {p.label}
                </span>
              ))}
            </div>
            {user && voucherSummary(user.vouchers) && (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: 'rgba(253,230,138,0.85)',
                          display: 'flex', alignItems: 'center', gap: 6 }}>
                <Ticket size={13} /> Tus bonos: {voucherSummary(user.vouchers)}
              </p>
            )}
          </div>

          {/* Wallet compacto — desktop only */}
          <div className="shop-pg-header-wallet" style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center', gap: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(0,30,60,0.78), rgba(0,12,28,0.8))',
            border: '1px solid rgba(0,229,255,0.35)',
            boxShadow: '0 0 24px rgba(0,180,255,0.12), inset 0 1px 0 rgba(255,255,255,0.04)',
            minWidth: 260,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'radial-gradient(circle at 35% 30%, rgba(0,229,255,0.5), rgba(0,80,150,0.45) 60%, transparent)',
              border: '1px solid rgba(0,229,255,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Snowflake size={20} style={{ color: '#cdf6ff', filter: 'drop-shadow(0 0 7px rgba(0,229,255,0.7))' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              <b style={{
                fontFamily: 'Cinzel, serif',
                fontSize: 18, color: '#fff',
                letterSpacing: '0.03em', lineHeight: 1.05,
              }}>
                {loadingUser ? '—' : (user ? crystals.toLocaleString() : '—')}
              </b>
              <span style={{
                fontSize: 9.5, color: 'rgba(0,229,255,0.7)',
                letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600,
              }}>
                tus cristales
              </span>
            </div>
            <button
              onClick={() => user ? setShowBuyCrystals(true) : (window.location.href = '/login')}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid rgba(0,229,255,0.4)',
                background: 'rgba(0,229,255,0.1)',
                color: '#00e5ff',
                fontSize: 10.5, fontWeight: 700,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'background 0.2s ease, color 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#00e5ff'; e.currentTarget.style.color = '#021019'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,229,255,0.1)'; e.currentTarget.style.color = '#00e5ff'; }}
            >
              <Plus size={11} strokeWidth={2.4} />
              Recargar
            </button>
          </div>
        </header>

        {/* ─── Alerts ─── */}
        <div className="shop-alerts">
        {paymentSuccess && (
          <div className="anim-fade-up" style={{
            marginBottom: 20, padding: 20, borderRadius: 12,
            background: 'rgba(255,185,0,0.07)', border: '1px solid rgba(255,185,0,0.35)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Check size={18} style={{ color: '#fbbf24', marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: '#fde68a', marginBottom: 4 }}>
                  ¡Gracias por apoyar Frostbound!
                </p>
                <p style={{ color: 'rgba(203,213,225,1)', fontSize: 14, lineHeight: 1.5 }}>
                  Tu contribución ayuda a mantener el servidor vivo. Si has aportado <strong style={{ color: '#fff' }}>5€ o más</strong>, tienes derecho a un NPC con tu nombre en el mundo del juego. Habla con nosotros en Discord y lo hacemos realidad.
                </p>
                <a
                  href="https://discord.com/invite/RwpMfRdzex"
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    marginTop: 12, padding: '6px 16px', borderRadius: 8,
                    fontSize: 12, fontWeight: 700,
                    background: 'rgba(88,101,242,0.25)',
                    border: '1px solid rgba(88,101,242,0.5)',
                    color: '#a5b4fc', textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={12} /> Ir al Discord
                </a>
              </div>
              <button onClick={() => setPaymentSuccess(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(100,116,139,1)', fontSize: 18, lineHeight: 1,
              }}>✕</button>
            </div>
          </div>
        )}

        {successMsg && (
          <div className="anim-fade-up" style={{
            marginBottom: 16, padding: '12px 16px', borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 14, color: 'rgb(134,239,172)',
            background: 'rgba(30,180,60,0.12)', border: '1px solid rgba(30,180,60,0.3)',
          }}>
            <Check size={15} /> {successMsg}
          </div>
        )}
        </div>{/* /shop-alerts */}

        {/* ─── NPC banner (morado) ─── */}
        <div className="shop-npc-wrap">
        <div
          className="anim-fade-up shop-npc-banner"
          style={{
            position: 'relative', overflow: 'hidden',
            borderRadius: 14, marginBottom: 26,
            background:
              'linear-gradient(100deg, rgba(8,4,18,0.95) 0%, rgba(28,12,52,0.85) 35%, rgba(36,16,68,0.7) 65%, rgba(24,10,42,0.6) 100%), radial-gradient(circle at 75% 50%, rgba(168,85,247,0.22), transparent 60%), #0c0820',
            border: '1px solid rgba(168,85,247,0.4)',
            boxShadow: '0 0 36px rgba(168,85,247,0.12), inset 0 0 0 1px rgba(168,85,247,0.08)',
            display: 'grid', gridTemplateColumns: '1fr auto',
            gap: 32, padding: '24px 32px', minHeight: 200,
          }}
        >
          {/* Imagen de fondo opcional — fallback al gradient si no existe */}
          <img
            src="/images/shop/npc-banner.jpg"
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'right center', zIndex: 0,
            }}
          />
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
            background:
              'linear-gradient(100deg, rgba(8,4,18,0.94) 0%, rgba(20,10,40,0.78) 42%, rgba(24,12,48,0.32) 78%, rgba(24,12,48,0.12) 100%), radial-gradient(circle at 75% 50%, rgba(168,85,247,0.18), transparent 60%)',
          }} />

          <div style={{ position: 'relative', zIndex: 2, maxWidth: 540, alignSelf: 'center' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontSize: 10.5, color: '#a855f7',
              letterSpacing: '0.22em', fontWeight: 700,
              textTransform: 'uppercase', marginBottom: 10,
            }}>
              <Star size={12} fill="#a855f7" /> Forma parte del servidor
            </div>
            <div style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 28, color: '#fff',
              textShadow: '0 0 18px rgba(168,85,247,0.35), 0 4px 14px rgba(0,0,0,0.7)',
              letterSpacing: '0.04em', lineHeight: 1.05, fontWeight: 700,
            }}>
              Deja tu nombre en{' '}
              <span style={{ color: '#e0bdff' }}>Azeroth</span>,
              para siempre.
            </div>
            <p style={{
              marginTop: 10, color: 'rgba(230,210,255,0.72)',
              fontSize: 13.5, lineHeight: 1.55, maxWidth: 480,
            }}>
              Cada apoyo de{' '}
              <strong style={{ color: '#e0bdff' }}>500 cristales o más</strong>{' '}
              da derecho a introducir un NPC con tu nombre y aspecto en Azeroth.
              Lo eliges tú: selecciona modelo, nombre y localización, y se queda plasmado en Frostbound. Para siempre.
            </p>
          </div>

          <div className="shop-npc-actions" style={{
            position: 'relative', zIndex: 2,
            display: 'flex', flexDirection: 'column', gap: 8,
            alignItems: 'flex-end', justifyContent: 'center',
          }}>
            <a
              className="shop-npc-discord-btn"
              href="https://discord.com/invite/RwpMfRdzex"
              target="_blank" rel="noopener noreferrer"
              style={{
                padding: '11px 22px', borderRadius: 9999,
                background: 'linear-gradient(135deg, rgba(168,85,247,0.85), rgba(100,40,170,0.85))',
                color: '#fff',
                border: '1px solid rgba(200,150,255,0.6)',
                fontFamily: 'Cinzel, serif',
                fontSize: 11.5, fontWeight: 700,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                cursor: 'pointer', textDecoration: 'none',
                boxShadow: '0 0 24px rgba(168,85,247,0.4)',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              <ExternalLink size={13} /> Hablar por Discord
            </a>
            <span className="shop-npc-note" style={{
              fontSize: 10.5, color: 'rgba(200,160,255,0.55)',
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              Aporte a partir de 500 cristales
            </span>
          </div>
        </div>

        </div>{/* /shop-npc-wrap */}

        {/* ─── Mobile category chips (sticky, hidden on desktop via CSS) ─── */}
        <div className="shop-cats-sticky">
        <div className="shop-cats-mobile">
          {[
            ...CHAPTERS.filter(ch => !ch.locked).map(ch => ({ label: CATEGORY_TREE[ch.key]?.label ?? ch.key, key: ch.key })),
          ].map(({ label, key, locked }) => (
            <button
              key={label}
              className={`shop-cat-chip${activeMain === key && !locked ? ' active' : ''}${locked ? ' locked' : ''}`}
              onClick={() => {
                if (locked) return;
                if (key === null) { setActiveMain(null); setActiveSub(null); }
                else handleMainChange(key);
              }}
            >
              {locked && <Lock size={11} />}
              {label}
            </button>
          ))}
        </div>
        </div>{/* /shop-cats-sticky */}

        {/* ─── Catálogo (rail + content) ─── */}
        <div className="shop-shell-grid">
          <CatalogRail
            activeMain={activeMain}
            activeSub={activeSub}
            onSelectMain={handleMainChange}
            onSelectSub={setActiveSub}
            productCounts={productCounts}
          />

          <div style={{ minWidth: 0 }}>
            {chaptersToShow.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgba(100,116,139,1)', padding: '64px 0', fontSize: 14 }}>
                {t('shop.no_products')}
              </div>
            ) : (
              chaptersToShow.map((ch, idx) => {
                const subs = CATEGORY_TREE[ch.key]?.subs ?? [];
                const groups = subs
                  .map((s) => ({
                    key: s.key,
                    label: s.label,
                    sublabel: SUBCAT_SUBLABEL[s.key] ?? '',
                    items: visibleProducts.filter(
                      (p) => p.category === ch.key && p.subcategory === s.key &&
                             (!activeSub || activeSub === s.key)
                    ),
                  }))
                  .filter((g) => g.items.length > 0);

                if (groups.length === 0) return null;

                return (
                  <React.Fragment key={ch.key}>
                    <ChapterDivider
                      num={ch.num}
                      title={ch.title}
                      blurb={ch.blurb}
                      isFirst={idx === 0}
                    />
                    {groups.map((g) => (
                      <div key={g.key} style={{ marginBottom: 32 }}>
                        <SectionHead
                          title={g.label.toLowerCase()}
                          sublabel={g.sublabel}
                          count={g.items.length}
                        />
                        <div className="shop-tt-grid">
                          {g.items.map((p) => (
                            <ProductCard
                              key={p.id}
                              product={p}
                              onBuy={handleBuy}
                              isLoggedIn={!!user}
                              vouchersLeft={p.voucher_redeemable ? (user?.vouchers?.[p.id] ?? 0) : 0}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      </main>

      <Footer />

      <BuyModal
        product={selectedProduct}
        user={user}
        crystals={crystals}
        onClose={() => setSelectedProduct(null)}
        onSuccess={handleSuccess}
      />

      <BuyCrystalsModal
        open={showBuyCrystals}
        onClose={() => setShowBuyCrystals(false)}
        user={user}
        crystals={crystals}
      />

      {/* Layout responsivo: rail + grid */}
      <style>{`
        .shop-shell-grid {
          display: grid;
          grid-template-columns: 240px 1fr;
          gap: 22px;
          align-items: start;
        }
        .shop-tt-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }
        @media (max-width: 1024px) {
          .shop-shell-grid { grid-template-columns: 1fr; }
          .shop-shell-grid > aside { position: static; }
          .shop-tt-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </PageWrapper>
  );
}
